import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback; receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Global error boundary. Festival users on spotty connections hit errors
 * constantly — never show them a blank screen. Catches render-time errors and
 * offers a friendly recovery UI (retry without a full reload, or hard reload).
 *
 * Note: error boundaries only catch errors thrown during render/lifecycle, not
 * inside event handlers or async callbacks — those are handled by each hook's
 * explicit error state.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hook for a real error reporter later; console keeps it visible for now.
    console.error('Uncaught error in component tree', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-ink-900 px-6 text-center text-white">
        <div className="text-6xl" aria-hidden="true">
          ✨
        </div>
        <h1 className="text-3xl font-bold">Something glitched</h1>
        <p className="max-w-sm text-white/70">
          The vibes got interrupted. Connections at festivals are rough — give it
          another tap.
        </p>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={this.reset}
            className="tap-target rounded-full bg-neon-pink px-8 py-3 text-lg font-semibold text-black"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="tap-target rounded-full px-8 py-3 text-lg font-semibold text-white/80 underline"
          >
            Reload the page
          </button>
        </div>
      </div>
    )
  }
}
